import { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Settings, RefreshCw, TrendingUp, Plus, Minus, Check, X, Clock, FileText, AlertTriangle } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useToast } from '../../components/Toast';
import { Modal } from '../../components/Modal';
import { formatMoeda } from '@fiodecobre/shared';
import { LinhaDiaPonto, type DiaPonto } from './ponto-batidas';

const MESES = [
  'Janeiro', 'Fevereiro', 'Marco', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

const fmt = (v: number | string | null | undefined): string =>
  Number(v ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Formata data plain (YYYY-MM-DD ou YYYY-MM-DDTHH...Z) sem aplicar conversao de timezone do browser.
function fmtDataPlain(iso: string): string {
  const d = new Date(iso);
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const yyyy = d.getUTCFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

interface PremioSemana {
  semana_num: number;
  dia_inicio: number;
  dia_fim: number;
  meta_valor: number;
  premio_valor: number;
  recebido: number;
  atingiu: boolean;
  status: string;
  atrasos: number;
  anomalias: number;
  total_avisos: number;
  bonus_perdido_avisos: boolean;
  atraso_minutos: number;
  teto_minutos: number;
  bonus_perdido_minutos: boolean;
  premio: number;
}

interface Vendedor {
  vendedor_id: number;
  vendedor_nome: string;
  total_recebido: number;
  meta_mensal: number;
  pct_meta: number;
  faixa_percentual: number;
  valor_comissao: number;
  premios_semanas: PremioSemana[];
  total_premios_semanas: number;
  bonus_loja: number;
  total_receber: number;
}

interface RateioLoja { percentual: number; total_loja: number; bolo: number; divisor: number; valor_cada: number }

interface Calculo {
  config: { mes: number; ano: number; meta_loja: number; bonus_meta_loja: number };
  modo: 'individual' | 'loja';
  total_loja: number;
  meta_loja_atingida: boolean;
  comissao_loja: RateioLoja | null;
  faixas: Array<{ valor_min: number; valor_max: number | null; percentual: number }>;
  vendedores: Vendedor[];
}

interface ExtratoLinha { documento: string; cliente: string; data: string; forma_pagamento: string; valor: number }

type EstadoTitulo = 'sem_comissao' | 'sem_fornecedor' | 'pendente' | 'gerado' | 'divergente' | 'pago';
type EstadoSemana = 'em_aberto' | 'nao_ganho' | 'pendente' | 'gerado' | 'pago';
interface SemanaEstado {
  semana_num: number;
  dia_inicio: number;
  dia_fim: number;
  premio: number;
  estado: EstadoSemana;
  titulo: { id: number; numero: string; valor: number; status: string } | null;
}
interface EstadoVendedora {
  vendedor_id: number;
  valor_mensal: number;       // base do titulo MENSAL = comissao da faixa + bonus loja
  tem_fornecedor: boolean;
  estado: EstadoTitulo;       // estado do titulo MENSAL
  titulo: { id: number; numero: string; valor: number; status: string; data_vencimento: string } | null;
  semanas: SemanaEstado[];
}
interface TitulosEstado {
  ano: number; mes: number; competencia: string; geracao_automatica: boolean;
  vendedores: EstadoVendedora[];
}

// Tipos que o modal de geracao oferece. 'mensal' = comissao da faixa; bonus_loja = parte do bonus.
type TipoGeracao = 'mensal' | 'bonus_loja' | 'semanal';

export function ComissoesPainel() {
  const { usuario } = useAuth();
  const toast = useToast();
  const isAdmin = usuario?.perfil === 'admin' || usuario?.perfil === 'gerente';

  const hoje = new Date();
  const [ano, setAno] = useState(hoje.getFullYear());
  const [mes, setMes] = useState(hoje.getMonth() + 1);
  // [DA-455] Consulta de UM dia. '' = mes inteiro (a tela de sempre). O dia NAO entra no
  // `calcularComissoes`: meta, faixa, comissao, premio e titulo sao do MES, e deixar um filtro de
  // tela mexer no que gera pagamento e o defeito que custou caro no [DA-450]. Aqui ele so responde
  // "quanto cada uma recebeu neste dia".
  const [dia, setDia] = useState<number | ''>('');
  const [diaDados, setDiaDados] = useState<{ total: number; linhas: Array<{ vendedor_id: number; nome: string; ativo: boolean; venda: number; crediario: number; total: number }> } | null>(null);
  const [diaLoading, setDiaLoading] = useState(false);
  const diasNoMes = new Date(ano, mes, 0).getDate();

  const [dados, setDados] = useState<Calculo | null>(null);
  const [loading, setLoading] = useState(false);
  const [semConfig, setSemConfig] = useState(false);

  // Expansoes hierarquicas: vendedor -> semanas -> vendas
  const [vendedoresAbertos, setVendedoresAbertos] = useState<Set<number>>(() => new Set());
  const [semanasAbertas, setSemanasAbertas] = useState<Set<string>>(() => new Set());
  const [extratosCache, setExtratosCache] = useState<Record<number, ExtratoLinha[]>>({});
  const [extratoLoading, setExtratoLoading] = useState<Record<number, boolean>>({});

  // Sequencia de ponto por vendedora (so admin) — carregada (mes inteiro) ao abrir uma semana,
  // e filtrada pra a semana dentro do "+" dela. 1 fetch por vendedora, reaproveitado nas semanas.
  const [pontoCache, setPontoCache] = useState<Record<number, DiaPonto[]>>({});
  const [pontoLoading, setPontoLoading] = useState<Record<number, boolean>>({});

  // Titulos de comissao (Contas a Pagar) — so admin/gerente. Estado por vendedora.
  const [titulos, setTitulos] = useState<Record<number, EstadoVendedora>>({});
  const [geracaoAuto, setGeracaoAuto] = useState<boolean | null>(null);
  const [gerando, setGerando] = useState(false);

  // Modal de selecao do que gerar. `alvo` = vendedoras (undefined = todas).
  const [modalGerar, setModalGerar] = useState<{ vendedorIds?: number[]; nome?: string; metaBatida?: boolean } | null>(null);

  const anosOpts: number[] = [];
  for (let y = hoje.getFullYear() - 1; y <= hoje.getFullYear() + 1; y++) anosOpts.push(y);

  async function carregar() {
    setLoading(true);
    setSemConfig(false);
    setVendedoresAbertos(new Set());
    setSemanasAbertas(new Set());
    setExtratosCache({});
    setPontoCache({});
    try {
      const r = await api.get<Calculo>(`/api/comissoes/calculo/${ano}/${mes}`);
      setDados(r);
    } catch (err: any) {
      if (err instanceof ApiError && err.status === 404) setSemConfig(true);
      else toast.erro(err.message);
      setDados(null);
    } finally {
      setLoading(false);
    }
  }

  async function carregarTitulos() {
    if (!isAdmin) return;
    try {
      const r = await api.get<TitulosEstado>(`/api/comissoes/titulos/${ano}/${mes}`);
      const map: Record<number, EstadoVendedora> = {};
      for (const v of r.vendedores) map[v.vendedor_id] = v;
      setTitulos(map);
      setGeracaoAuto(r.geracao_automatica);
    } catch {
      setTitulos({});
      setGeracaoAuto(null);
    }
  }

  async function gerarTitulos(tipos: TipoGeracao[], vendedorIds?: number[]) {
    setGerando(true);
    try {
      const r = await api.post<{ gerados: number; regerados: number; resultados: Array<{ acao: string; vendedor_nome: string }> }>(
        '/api/comissoes/gerar-titulo',
        { ano, mes, vendedor_ids: vendedorIds, tipos },
      );
      const semForn = r.resultados.filter((x) => x.acao === 'sem_fornecedor');
      const partes: string[] = [];
      if (r.gerados) partes.push(`${r.gerados} gerado(s)`);
      if (r.regerados) partes.push(`${r.regerados} regerado(s)`);
      if (!r.gerados && !r.regerados) partes.push('nada a gerar (ja em dia)');
      let msg = `Titulos: ${partes.join(', ')}.`;
      if (semForn.length) msg += ` Sem fornecedor: ${[...new Set(semForn.map((x) => x.vendedor_nome))].join(', ')}.`;
      toast.sucesso(msg);
      await carregarTitulos();
    } catch (err: any) {
      toast.erro(err.message);
    } finally {
      setGerando(false);
      setModalGerar(null);
    }
  }

  useEffect(() => { void carregar(); void carregarTitulos(); }, [ano, mes]);

  // [DA-455] Trocar de mes com um dia 31 escolhido apontaria pra um dia que nao existe (30/02).
  // Corrige o dia ANTES de consultar, em vez de deixar o backend recusar com 400.
  useEffect(() => { if (dia !== '' && dia > diasNoMes) setDia(diasNoMes); }, [diasNoMes]);

  useEffect(() => {
    if (dia === '') { setDiaDados(null); return; }
    let vivo = true;
    setDiaLoading(true);
    api.get<NonNullable<typeof diaDados>>(`/api/comissoes/dia/${ano}/${mes}/${dia}`)
      .then((r) => { if (vivo) setDiaDados(r); })
      .catch(() => { if (vivo) setDiaDados({ total: 0, linhas: [] }); })
      .finally(() => { if (vivo) setDiaLoading(false); });
    return () => { vivo = false; }; // resposta de um dia antigo nao sobrescreve o dia atual
  }, [ano, mes, dia]);

  async function carregarExtrato(vid: number) {
    setExtratoLoading((p) => ({ ...p, [vid]: true }));
    try {
      const r = await api.get<{ linhas: ExtratoLinha[]; total: number }>(`/api/comissoes/extrato/${ano}/${mes}/${vid}`);
      setExtratosCache((p) => ({ ...p, [vid]: r.linhas }));
    } catch {
      setExtratosCache((p) => ({ ...p, [vid]: [] }));
    } finally {
      setExtratoLoading((p) => ({ ...p, [vid]: false }));
    }
  }

  function toggleVendedor(vid: number) {
    setVendedoresAbertos((prev) => {
      const next = new Set(prev);
      if (next.has(vid)) {
        next.delete(vid);
        // Fecha todas as semanas expandidas desse vendedor
        setSemanasAbertas((sp) => {
          const ns = new Set(sp);
          for (const k of ns) if (k.startsWith(`${vid}:`)) ns.delete(k);
          return ns;
        });
      } else {
        next.add(vid);
        if (!extratosCache[vid]) void carregarExtrato(vid);
      }
      return next;
    });
  }

  async function carregarPonto(vid: number) {
    setPontoLoading((p) => ({ ...p, [vid]: true }));
    try {
      const inicio = `${ano}-${String(mes).padStart(2, '0')}-01`;
      const ultimoDia = new Date(ano, mes, 0).getDate();
      const fim = `${ano}-${String(mes).padStart(2, '0')}-${String(ultimoDia).padStart(2, '0')}`;
      const r = await api.get<DiaPonto[]>(`/api/ponto/sequencia?usuario_id=${vid}&inicio=${inicio}&fim=${fim}`);
      setPontoCache((p) => ({ ...p, [vid]: r }));
    } catch {
      setPontoCache((p) => ({ ...p, [vid]: [] }));
    } finally {
      setPontoLoading((p) => ({ ...p, [vid]: false }));
    }
  }

  function toggleSemana(vid: number, num: number) {
    const k = `${vid}:${num}`;
    setSemanasAbertas((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else {
        next.add(k);
        if (!pontoCache[vid]) void carregarPonto(vid); // carrega o ponto do mes pra filtrar nesta semana
      }
      return next;
    });
  }

  function linhasDaSemana(vid: number, diaInicio: number, diaFim: number): ExtratoLinha[] {
    const linhas = extratosCache[vid] ?? [];
    return linhas.filter((l) => {
      const m = String(l.data).match(/^\d{4}-\d{2}-(\d{2})/);
      if (!m) return false;
      const dia = parseInt(m[1]!, 10);
      return dia >= diaInicio && dia <= diaFim;
    });
  }

  return (
    <div className="py-4 space-y-4">
      <div className="pagina-header" style={{ flexWrap: 'wrap' }}>
        <h2 className="pagina-titulo">
          <TrendingUp size={20} style={{ display: 'inline', marginRight: 6, marginBottom: -3 }} />
          Comissoes
          {dados && (
            <span
              className="text-xs px-2 py-0.5 rounded-full font-medium ml-2 align-middle"
              style={dados.modo === 'loja'
                ? { backgroundColor: '#fde68a', color: '#000' }
                : { backgroundColor: 'var(--card-bg, #f3f4f6)', color: 'var(--text-muted, #6b7280)', border: '1px solid var(--border, #e5e7eb)' }}
              title={dados.modo === 'loja'
                ? 'Comissao Pela Loja: faixa sobre o faturamento real, rateada igualmente entre as vendedoras com meta'
                : 'Comissao Individual: faixa sobre o volume de cada vendedora'}
            >
              {dados.modo === 'loja' ? 'Pela Loja' : 'Individual'}
            </span>
          )}
        </h2>
        <div className="pagina-acoes">
          {/* [DA-455] Dia em branco = o mes inteiro (comportamento de sempre). Vem ANTES do mes pra
              ler na ordem em que se fala a data. */}
          <select
            value={dia}
            onChange={(e) => setDia(e.target.value === '' ? '' : Number(e.target.value))}
            className="border rounded px-2 py-1.5 text-sm"
            title="Dia em branco = o mes inteiro"
          >
            <option value="">Dia —</option>
            {Array.from({ length: diasNoMes }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <select value={mes} onChange={(e) => setMes(Number(e.target.value))} className="border rounded px-2 py-1.5 text-sm">
            {MESES.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
          </select>
          <select value={ano} onChange={(e) => setAno(Number(e.target.value))} className="border rounded px-2 py-1.5 text-sm">
            {anosOpts.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <button type="button" onClick={() => void carregar()} className="p-1.5 text-gray-500 hover:text-gray-700 border rounded" title="Atualizar">
            <RefreshCw size={15} />
          </button>
          {/* Gerar Titulos some com o dia filtrado: ele paga o MES, e um botao de pagamento ao lado
              de um recorte de um dia so convida ao erro ([DA-450] de novo). */}
          {isAdmin && dia === '' && (
            <button
              type="button"
              onClick={() => setModalGerar({ metaBatida: dados?.meta_loja_atingida ?? false })}
              disabled={gerando || !dados || dados.vendedores.length === 0}
              className="flex items-center gap-1.5 border rounded px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              title="Gera (ou atualiza) os titulos de comissao em Contas a Pagar"
            >
              <FileText size={14} /> {gerando ? 'Gerando...' : 'Gerar Titulos'}
            </button>
          )}
          {isAdmin && (
            <Link to={`/comissoes/configurar?mes=${mes}&ano=${ano}`} className="flex items-center gap-1.5 border rounded px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50" style={{ textDecoration: 'none' }}>
              <Settings size={14} /> Configurar
            </Link>
          )}
        </div>
      </div>

      {isAdmin && geracaoAuto === false && (
        <div className="text-xs px-3 py-2 rounded" style={{ backgroundColor: '#fde68a', color: '#000' }}>
          Geracao automatica de titulos <strong>desligada</strong>. Os titulos so saem pelo botao "Gerar Titulos". Religue em Configurar.
        </div>
      )}

      {dia === '' && loading && <div className="text-center text-black py-10">Calculando...</div>}

      {/* Mes sem configuracao nao impede a consulta do dia: ela nao usa meta nem faixa. Mostrar o
          aviso ali diria "nao da pra ver" bem ao lado da tabela que esta vendo. */}
      {dia === '' && !loading && semConfig && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <p className="text-yellow-700 font-medium mb-2">Nenhuma configuracao para {MESES[mes - 1]} {ano}.</p>
          {isAdmin && (
            <Link to="/comissoes/configurar" className="mt-1 inline-block bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700" style={{ textDecoration: 'none' }}>
              Configurar agora
            </Link>
          )}
        </div>
      )}

      {/* [DA-455] CONSULTA DE UM DIA. Substitui a tela mensal em vez de conviver com ela: meta,
          faixa, comissao e premio sao do MES e, exibidos ao lado de um recorte de um dia, dariam
          leituras falsas (o "% Meta" compararia um dia contra a meta mensal). Some tudo e sobra a
          unica pergunta que o dia responde. */}
      {dia !== '' && (
        <>
          <div className="text-sm text-gray-500">
            Recebido em <strong className="text-gray-700">{String(dia).padStart(2, '0')}/{String(mes).padStart(2, '0')}/{ano}</strong>
            {' '}— meta, faixa e comissao seguem do mes (limpe o dia pra ve-las)
          </div>

          {diaLoading ? (
            <div className="text-center text-black py-10">Calculando...</div>
          ) : !diaDados || diaDados.linhas.length === 0 ? (
            <p className="text-center text-gray-400 py-6 text-sm">Nenhum recebimento neste dia.</p>
          ) : (
            <>
              {/* Mesma moldura da tabela mensal ([DA-455]): cabecalho navy, zebra e larguras iguais,
                  pra a leitura nao mudar de dialeto entre os dois modos. As colunas do MES (Meta,
                  Faixa, Comissao, S1..S6, Premios, Bonus, Titulo) nao vem: elas nao existem pra um
                  dia, e enche-las de "—" seria pior que omitir — sugeriria que ha valor a apurar. */}
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ backgroundColor: 'var(--grid-header-bg, #1e3a8a)', color: 'var(--grid-header-fg, #fff)' }}>
                      <th className="text-left px-4 py-2.5 font-semibold">Vendedor</th>
                      {/* De onde veio o dinheiro: venda finalizada no dia x parcela de crediario paga
                          no dia (pode ser de venda antiga — por isso quem ja saiu da loja aparece). */}
                      <th className="text-right px-4 py-2.5 font-semibold">Vendas do dia</th>
                      <th className="text-right px-4 py-2.5 font-semibold">Parcelas de crediario</th>
                      <th className="text-right px-4 py-2.5 font-semibold">Recebido</th>
                    </tr>
                  </thead>
                  <tbody>
                    {diaDados.linhas.map((l, idx) => (
                      <tr key={l.vendedor_id} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f9fafb' }}>
                        <td className="px-4 py-2.5 font-medium">
                          {l.nome}
                          {!l.ativo && <span style={{ fontSize: 11, color: '#6b7280', marginLeft: 6, fontWeight: 400 }}>(inativa)</span>}
                        </td>
                        <td className="cel-num text-right px-4 py-2.5">{l.venda > 0.005 ? `R$ ${fmt(l.venda)}` : '—'}</td>
                        <td className="cel-num text-right px-4 py-2.5">{l.crediario > 0.005 ? `R$ ${fmt(l.crediario)}` : '—'}</td>
                        <td className="cel-num text-right px-4 py-2.5">R$ {fmt(l.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Espelha o cartao "Total a pagar em comissoes" do mes, mesma posicao e peso. */}
              <div className="flex justify-end">
                <div className="bg-blue-50 border border-blue-100 rounded-lg px-5 py-3 text-sm">
                  <span className="text-gray-600">Total recebido no dia:</span>{' '}
                  <strong className="cel-num text-blue-700 text-lg">R$ {fmt(diaDados.total)}</strong>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {dia === '' && !loading && dados && (
        <>
          <MetaLojaCard dados={dados} />

          {dados.modo === 'loja' && dados.comissao_loja && <RateioLojaCard dados={dados} />}

          {/* Faixas como pilulas */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-gray-500">{dados.modo === 'loja' ? 'Faixas da loja:' : 'Faixas:'}</span>
            {dados.faixas.length === 0 && <span className="text-xs text-gray-400">nenhuma faixa configurada</span>}
            {dados.faixas.map((f, i) => (
              <span key={i} className="cel-num bg-blue-50 text-blue-700 text-xs px-2.5 py-1 rounded-full border border-blue-100">
                {f.valor_max ? `R$ ${fmt(f.valor_min)} – R$ ${fmt(f.valor_max)}` : `Acima de R$ ${fmt(f.valor_min)}`}
                {' → '}{f.percentual}%
              </span>
            ))}
          </div>

          {dados.vendedores.length === 0 ? (
            <p className="text-center text-gray-400 py-6 text-sm">Nenhum vendedor configurado para este mes.</p>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
              <table className="w-full text-sm min-w-[900px]">
                <thead>
                  <tr style={{ backgroundColor: 'var(--grid-header-bg, #1e3a8a)', color: 'var(--grid-header-fg, #fff)' }}>
                    <th className="text-left px-4 py-2.5 font-semibold">Vendedor</th>
                    {/* DA-438: e' recebimento, nao venda — o nome da casa e' "vendas recebidas" (pedido dele, 03/11/2026). */}
                    <th className="text-right px-3 py-2.5 font-semibold">Vendas<br />Recebidas</th>
                    <th className="text-right px-3 py-2.5 font-semibold">Meta<br />Mensal</th>
                    <th className="text-right px-3 py-2.5 font-semibold">% Meta</th>
                    <th className="text-right px-3 py-2.5 font-semibold">Faixa</th>
                    {dados.vendedores[0]?.premios_semanas.map((s) => (
                      <th key={s.semana_num} className="text-center px-2 py-2.5 font-semibold text-xs">
                        S{s.semana_num}
                        <div className="text-[10px] font-normal opacity-80">{s.dia_inicio}/{s.dia_fim}</div>
                      </th>
                    ))}
                    <th className="text-right px-3 py-2.5 font-semibold">Premios</th>
                    <th className="text-right px-3 py-2.5 font-semibold">Comissao</th>
                    <th className="text-right px-3 py-2.5 font-semibold">Bonus Loja</th>
                    <th className="text-right px-4 py-2.5 font-semibold">Total</th>
                    {isAdmin && <th className="text-center px-3 py-2.5 font-semibold">Titulo</th>}
                  </tr>
                </thead>
                <tbody>
                  {dados.vendedores.map((v, idx) => {
                    const bgRow = idx % 2 === 0 ? '#fff' : '#f9fafb';
                    const isOpen = vendedoresAbertos.has(v.vendedor_id);
                    const totalCols = (isAdmin ? 10 : 9) + v.premios_semanas.length;
                    const est = titulos[v.vendedor_id];
                    const semanasVisiveis = v.premios_semanas.filter((s) => s.status !== 'futura');
                    return (
                      <Fragment key={v.vendedor_id}>
                        <tr style={{ backgroundColor: bgRow }} className="hover:brightness-95 transition">
                          <td className="px-4 py-2.5 font-medium">
                            <button
                              type="button"
                              onClick={() => toggleVendedor(v.vendedor_id)}
                              className="inline-flex items-center justify-center w-5 h-5 mr-2 rounded border border-gray-300 text-gray-600 hover:bg-gray-200 transition align-middle"
                              title={isOpen ? 'Recolher' : 'Expandir semanas'}
                            >
                              {isOpen ? <Minus size={12} /> : <Plus size={12} />}
                            </button>
                            {v.vendedor_nome}
                          </td>
                          <td className="cel-num px-3 py-2.5 text-right font-mono">R$ {fmt(v.total_recebido)}</td>
                          <td className="cel-num px-3 py-2.5 text-right font-mono text-black">R$ {fmt(v.meta_mensal)}</td>
                          <td className="px-3 py-2.5 text-right">
                            <PctBadge pct={v.pct_meta} />
                          </td>
                          <td className="cel-num px-3 py-2.5 text-right">
                            {v.faixa_percentual > 0 ? (
                              <span className="font-semibold text-blue-700">{v.faixa_percentual}%</span>
                            ) : (
                              <span className="text-gray-400 text-xs">—</span>
                            )}
                          </td>
                          {v.premios_semanas.map((s) => {
                            const semEst = est?.semanas.find((x) => x.semana_num === s.semana_num);
                            return (
                              <td key={s.semana_num} className="px-2 py-2.5 text-center align-middle" title={tituloSemana(s, semEst?.estado)}>
                                <IconeStatusSemana
                                  status={s.status || (s.atingiu ? 'atingida' : 'nao-atingida')}
                                  bonusPerdido={s.bonus_perdido_avisos || s.bonus_perdido_minutos}
                                />
                                {isAdmin && semEst && <SeloSemana estado={semEst.estado} />}
                              </td>
                            );
                          })}
                          <td className="cel-num px-3 py-2.5 text-right font-mono">
                            {v.total_premios_semanas > 0
                              ? <span className="text-black">R$ {fmt(v.total_premios_semanas)}</span>
                              : <span className="text-black">—</span>}
                          </td>
                          <td className="cel-num px-3 py-2.5 text-right font-mono font-semibold text-black">
                            R$ {fmt(v.valor_comissao)}
                          </td>
                          <td className="cel-num px-3 py-2.5 text-right font-mono">
                            {v.bonus_loja > 0
                              ? <span className="text-black">R$ {fmt(v.bonus_loja)}</span>
                              : <span className="text-black">—</span>}
                          </td>
                          <td className="cel-num px-4 py-2.5 text-right font-mono font-bold text-blue-700">
                            R$ {fmt(v.total_receber)}
                          </td>
                          {isAdmin && (
                            <td className="px-3 py-2.5 text-center">
                              <TituloCell est={est} gerando={gerando} onGerar={() => setModalGerar({ vendedorIds: [v.vendedor_id], nome: v.vendedor_nome, metaBatida: dados?.meta_loja_atingida ?? false })} />
                            </td>
                          )}
                        </tr>

                        {isOpen && (
                          <tr key={`sem-${v.vendedor_id}`}>
                            <td colSpan={totalCols} className="px-6 py-3 bg-gray-50 border-b border-gray-200">
                              {extratoLoading[v.vendedor_id] ? (
                                <span className="text-gray-400 text-xs">Carregando vendas...</span>
                              ) : semanasVisiveis.length === 0 ? (
                                <span className="text-gray-400 text-xs">Nenhuma semana em curso ou encerrada ainda.</span>
                              ) : (
                                <div className="space-y-1">
                                  {semanasVisiveis.map((s) => {
                                    const semKey = `${v.vendedor_id}:${s.semana_num}`;
                                    const semOpen = semanasAbertas.has(semKey);
                                    const linhasSem = linhasDaSemana(v.vendedor_id, s.dia_inicio, s.dia_fim);
                                    const totalSem = linhasSem.reduce((a, l) => a + l.valor, 0);
                                    return (
                                      <div key={semKey} className="border border-gray-200 rounded bg-white">
                                        <button
                                          type="button"
                                          onClick={() => toggleSemana(v.vendedor_id, s.semana_num)}
                                          className="w-full flex items-center gap-3 px-3 py-1.5 text-[12.25px] text-black text-left hover:bg-gray-50"
                                        >
                                          <span className="inline-flex items-center justify-center w-5 h-5 rounded border border-gray-300 text-black">
                                            {semOpen ? <Minus size={11} /> : <Plus size={11} />}
                                          </span>
                                          <span className="font-medium text-black whitespace-nowrap">Semana {s.semana_num}</span>
                                          <span className="text-black whitespace-nowrap">Dias {s.dia_inicio}–{s.dia_fim}</span>
                                          <span className="cel-num font-mono text-black whitespace-nowrap">R$ {fmt(s.recebido)}</span>
                                          <span className="text-black">de</span>
                                          <span className="cel-num font-mono text-black whitespace-nowrap">R$ {fmt(s.meta_valor)}</span>
                                          <span className="ml-auto">
                                            <IconeStatusSemana
                                              status={s.status || (s.atingiu ? 'atingida' : 'nao-atingida')}
                                              bonusPerdido={s.bonus_perdido_avisos || s.bonus_perdido_minutos}
                                            />
                                          </span>
                                          {s.premio > 0 && (
                                            <span className="cel-num text-black font-semibold whitespace-nowrap">+ R$ {fmt(s.premio)}</span>
                                          )}
                                        </button>
                                        {semOpen && (
                                          <div className="px-4 py-2 border-t border-gray-100 bg-gray-50 space-y-3">
                                            {isAdmin && (
                                              <div>
                                                <div className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-600 mb-1">
                                                  <Clock size={12} className="text-gray-500" /> Ponto da semana
                                                </div>
                                                <PontoDaSemana
                                                  dias={pontoCache[v.vendedor_id]}
                                                  loading={!!pontoLoading[v.vendedor_id]}
                                                  semana={s}
                                                />
                                              </div>
                                            )}
                                            <div>
                                              <div className="text-[11px] font-semibold text-gray-600 mb-1">Vendas recebidas</div>
                                            {linhasSem.length === 0 ? (
                                              <span className="text-gray-400 text-xs">Nenhuma venda recebida nesta semana.</span>
                                            ) : (
                                              <table className="text-xs w-full">
                                                <thead>
                                                  <tr className="text-gray-500 border-b">
                                                    <th className="text-left pb-1 pr-4">Doc</th>
                                                    <th className="text-left pb-1 pr-4">Cliente</th>
                                                    <th className="text-left pb-1 pr-4">Data</th>
                                                    <th className="text-left pb-1 pr-4">Forma</th>
                                                    <th className="text-right pb-1">Valor</th>
                                                  </tr>
                                                </thead>
                                                <tbody>
                                                  {linhasSem.map((l, li) => (
                                                    <tr key={li} className="border-b last:border-0">
                                                      <td className="py-0.5 pr-4 font-mono">{l.documento}</td>
                                                      <td className="py-0.5 pr-4">{l.cliente}</td>
                                                      <td className="py-0.5 pr-4 whitespace-nowrap">{fmtDataPlain(l.data)}</td>
                                                      <td className="py-0.5 pr-4">{l.forma_pagamento}</td>
                                                      <td className="cel-num py-0.5 text-right font-mono">R$ {fmt(l.valor)}</td>
                                                    </tr>
                                                  ))}
                                                  <tr className="font-semibold">
                                                    <td colSpan={4} className="pt-1 text-right pr-4 text-gray-600">Total:</td>
                                                    <td className="cel-num pt-1 text-right font-mono text-green-700">R$ {fmt(totalSem)}</td>
                                                  </tr>
                                                </tbody>
                                              </table>
                                            )}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {dados.vendedores.length > 0 && (
            <div className="flex justify-end">
              <div className="bg-white border border-gray-200 rounded-lg px-6 py-3 text-sm">
                <span className="text-gray-500 mr-3">Total a pagar em comissoes:</span>
                <span className="cel-num-forte font-bold text-blue-700 text-lg">
                  R$ {fmt(dados.vendedores.reduce((a, v) => a + v.total_receber, 0))}
                </span>
              </div>
            </div>
          )}
        </>
      )}

      {modalGerar && (
        <ModalGerarTitulos
          alvoNome={modalGerar.nome}
          metaBatida={modalGerar.metaBatida ?? false}
          gerando={gerando}
          onFechar={() => setModalGerar(null)}
          onConfirmar={(tipos) => void gerarTitulos(tipos, modalGerar.vendedorIds)}
        />
      )}
    </div>
  );
}

// Modal de selecao do que gerar. Default = Comissao + Bonus loja (o mensal); Premio semanal
// desmarcado (sai sozinho no sabado), disponivel pra correcao manual. Nao fecha por overlay.
function ModalGerarTitulos({
  alvoNome, metaBatida, gerando, onFechar, onConfirmar,
}: {
  alvoNome?: string;
  metaBatida: boolean;
  gerando: boolean;
  onFechar: () => void;
  onConfirmar: (tipos: TipoGeracao[]) => void;
}) {
  const [comissao, setComissao] = useState(true);
  const [bonus, setBonus] = useState(metaBatida);
  const [semanal, setSemanal] = useState(false);

  const tipos: TipoGeracao[] = [];
  if (comissao) tipos.push('mensal');
  if (bonus) tipos.push('bonus_loja');
  if (semanal) tipos.push('semanal');
  const nenhum = tipos.length === 0;

  const Item = ({ checked, onChange, titulo, desc, disabled }: { checked: boolean; onChange: (v: boolean) => void; titulo: string; desc: string; disabled?: boolean }) => (
    <label
      className={`flex items-start gap-4 p-4 rounded border transition-colors ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
      style={{
        borderColor: disabled ? 'var(--panel-border, #e5e7eb)' : checked ? 'var(--primary, #1d4ed8)' : 'var(--panel-border, #e5e7eb)',
        backgroundColor: !disabled && checked ? 'color-mix(in srgb, var(--primary, #1d4ed8) 7%, transparent)' : undefined,
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => !disabled && onChange(e.target.checked)}
        className="mt-0.5 w-5 h-5 flex-shrink-0"
        style={{ accentColor: 'var(--primary, #1d4ed8)' }}
      />
      <span>
        <span className="block text-base font-semibold" style={{ color: 'var(--text-color, #111827)' }}>{titulo}</span>
        <span className="block text-sm text-gray-500">{desc}</span>
      </span>
    </label>
  );

  return (
    <Modal
      aberto
      onFechar={onFechar}
      titulo={alvoNome ? `Gerar titulos — ${alvoNome}` : 'Gerar titulos de comissao'}
      largura={560}
      rodape={
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onFechar} className="px-6 py-2.5 text-base rounded border border-gray-300 text-gray-600 hover:bg-gray-50">
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => onConfirmar(tipos)}
            disabled={gerando || nenhum}
            className="px-6 py-2.5 text-base rounded font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
          >
            {gerando ? 'Gerando...' : 'Gerar'}
          </button>
        </div>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-gray-500 mb-3">
          Marque o que gerar em Contas a Pagar. Comissao e Bonus loja viram um titulo mensal; o premio
          semanal e um titulo por semana ganha.
        </p>
        <Item checked={comissao} onChange={setComissao} titulo="Comissao (faixa)" desc="Percentual sobre o recebido — parte do titulo mensal." />
        <Item checked={bonus} onChange={setBonus} titulo="Bonus loja" desc={metaBatida ? 'Bonus por meta da loja batida — parte do titulo mensal.' : 'Meta da loja ainda nao atingida — nao disponivel.'} disabled={!metaBatida} />
        <Item checked={semanal} onChange={setSemanal} titulo="Premio semanal" desc="Um titulo por semana fechada e ganha. Normalmente sai sozinho no sabado; marque para corrigir na mao." />
        {nenhum && <p className="text-xs" style={{ color: '#b45309' }}>Selecione ao menos um tipo.</p>}
      </div>
    </Modal>
  );
}

function MetaLojaCard({ dados }: { dados: Calculo }) {
  const pct = dados.config.meta_loja > 0
    ? Math.min((dados.total_loja / dados.config.meta_loja) * 100, 100)
    : 0;
  const atingiu = dados.meta_loja_atingida;

  return (
    <div className={`rounded-lg border p-4 ${atingiu ? 'bg-green-50 border-green-300' : 'bg-white border-gray-200'}`}>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div>
          <span className="font-semibold text-gray-700 mr-3">Meta da Loja</span>
          {atingiu ? (
            <span className="bg-green-600 text-white text-xs px-2 py-0.5 rounded-full font-medium">
              ✓ Atingida! Bonus <span className="cel-num">R$ {fmt(dados.config.bonus_meta_loja)}</span> por vendedor
            </span>
          ) : (
            <span className="bg-gray-200 text-gray-600 text-xs px-2 py-0.5 rounded-full">Em andamento</span>
          )}
        </div>
        <div className="text-sm text-gray-600">
          <span className="cel-num font-mono font-semibold">R$ {fmt(dados.total_loja)}</span>
          <span className="text-gray-400 mx-1">/</span>
          <span className="cel-num font-mono">R$ {fmt(dados.config.meta_loja)}</span>
          <span className="cel-num ml-2 font-semibold">{pct.toFixed(1)}%</span>
          {!atingiu && dados.config.meta_loja > 0 && (
            <span className="cel-num ml-3 text-orange-600 font-mono">
              faltam R$ {fmt(dados.config.meta_loja - dados.total_loja)}
            </span>
          )}
        </div>
      </div>
      <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${atingiu ? 'bg-green-500' : 'bg-blue-500'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      {!atingiu && dados.config.meta_loja > 0 && (
        <p className="text-xs text-gray-500 mt-1">
          Faltam <span className="cel-num">R$ {fmt(dados.config.meta_loja - dados.total_loja)}</span> para atingir a meta
        </p>
      )}
    </div>
  );
}

// Card do modo Pela Loja: mostra como o bolo da loja vira a comissao igual de cada vendedora.
// Transparencia do rateio (a tela explica o estado, nao deixa o numero "cair do ceu").
function RateioLojaCard({ dados }: { dados: Calculo }) {
  const r = dados.comissao_loja!;
  return (
    <div className="rounded-lg border border-amber-300 p-4" style={{ backgroundColor: '#fffbeb' }}>
      <div className="flex items-center gap-2 mb-2">
        <span className="font-semibold text-gray-800">Rateio Pela Loja</span>
        <span className="text-xs text-gray-500">comissao igual para cada vendedora com meta</span>
      </div>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
        <span className="text-gray-600">Faturamento</span>
        <span className="cel-num font-mono font-semibold">R$ {fmt(r.total_loja)}</span>
        <span className="text-gray-400">×</span>
        <span className="cel-num font-mono font-semibold text-blue-700">{r.percentual}%</span>
        <span className="text-gray-400">=</span>
        <span className="text-gray-600">bolo</span>
        <span className="cel-num font-mono font-semibold">R$ {fmt(r.bolo)}</span>
        <span className="text-gray-400">÷</span>
        <span className="cel-num font-mono font-semibold">{r.divisor}</span>
        <span className="text-gray-600">com meta</span>
        <span className="text-gray-400">=</span>
        <span className="cel-num font-mono font-bold text-green-700">R$ {fmt(r.valor_cada)} cada</span>
      </div>
      {r.divisor === 0 && (
        <p className="text-xs mt-1" style={{ color: '#b45309' }}>
          Nenhuma vendedora com meta cadastrada — adicione metas em Configurar para dividir o bolo.
        </p>
      )}
      {r.percentual === 0 && r.total_loja > 0 && (
        <p className="text-xs mt-1" style={{ color: '#b45309' }}>
          Faturamento fora de todas as faixas da loja (comissao 0%). Confira se a primeira faixa comeca em R$ 0.
        </p>
      )}
    </div>
  );
}

function IconeStatusSemana({ status, bonusPerdido }: { status: string; bonusPerdido?: boolean }) {
  // Tudo em preto (pedido dele, 03/11/2026): a forma do icone diz o estado, nao a cor.
  if (bonusPerdido) return <X size={16} className="mx-auto text-black" strokeWidth={3} />;
  switch (status) {
    case 'atingida': return <Check size={16} className="mx-auto text-black" />;
    case 'nao-atingida': return <X size={16} className="mx-auto text-black" />;
    case 'em-andamento': return <Clock size={16} className="mx-auto text-black" />;
    case 'futura':
    case 'sem-meta':
    default: return <Minus size={16} className="mx-auto text-black" />;
  }
}

const ROTULO_SEMANA: Record<EstadoSemana, string> = {
  em_aberto: 'Semana em aberto',
  nao_ganho: 'Premio nao ganho',
  pendente: 'Premio a gerar',
  gerado: 'Premio gerado',
  pago: 'Premio pago',
};

function tituloSemana(s: PremioSemana, estadoTitulo?: EstadoSemana): string {
  const base = `Recebido: R$ ${fmt(s.recebido)} | Meta: R$ ${fmt(s.meta_valor)}`;
  const avisos = (Number(s.atrasos) || 0) + (Number(s.anomalias) || 0);
  const perdido = s.bonus_perdido_avisos || s.bonus_perdido_minutos;
  const min = Number(s.atraso_minutos) || 0;
  const sufMin = min > 0 ? ` · Atraso ${min} min/sem${s.teto_minutos > 0 ? `/${s.teto_minutos}` : ''}` : '';
  const sufAvisos = (avisos > 0 || min > 0)
    ? ` | Atrasos: ${s.atrasos || 0} · Anomalias: ${s.anomalias || 0}${sufMin}${perdido ? ' (BONUS PERDIDO)' : ''}`
    : '';
  const sufTitulo = estadoTitulo ? ` | Titulo: ${ROTULO_SEMANA[estadoTitulo]}` : '';
  if (s.status === 'futura') return `${base} | Semana futura${sufAvisos}${sufTitulo}`;
  if (s.status === 'em-andamento') return `${base} | Semana em andamento${sufAvisos}${sufTitulo}`;
  if (s.status === 'sem-meta') return `${base} | Sem meta configurada${sufAvisos}${sufTitulo}`;
  return `${base}${sufAvisos}${sufTitulo}`;
}

// Selo textual abaixo do icone S1-S6 com o estado do PREMIO daquela semana (titulo semanal).
// Rotulo textual SEMPRE (daltonismo-safe) — a cor so reforca. Em aberto/nao-ganho ficam discretos.
function SeloSemana({ estado }: { estado: EstadoSemana }) {
  if (estado === 'em_aberto') return null; // semana ainda nao fechou: nada a sinalizar
  const cfg: Record<Exclude<EstadoSemana, 'em_aberto'>, { txt: string; cls: string; style?: React.CSSProperties }> = {
    nao_ganho: { txt: 'sem premio', cls: 'text-black' },
    pendente: { txt: 'a gerar', cls: '', style: { backgroundColor: '#fde68a', color: '#000' } },
    gerado: { txt: 'gerado', cls: 'text-black' },
    pago: { txt: 'pago', cls: 'text-black' },
  };
  const c = cfg[estado];
  return (
    <div className={`text-[12.25px] leading-tight whitespace-nowrap mt-0.5 px-1 rounded ${c.cls}`} style={c.style}>
      {c.txt}
    </div>
  );
}

// Estado do titulo de comissao por vendedora. Rotulo de texto SEMPRE (nao depende de cor —
// daltonismo). Divergente/pendente oferecem o botao de gerar; pago/gerado so informam.
function TituloCell({ est, gerando, onGerar }: { est?: EstadoVendedora; gerando: boolean; onGerar: () => void }) {
  if (!est) return <span className="text-gray-300 text-xs">—</span>;

  const btn = (label: string) => (
    <button
      type="button"
      onClick={onGerar}
      disabled={gerando}
      className="text-xs px-2 py-1 rounded border border-blue-300 text-blue-700 hover:bg-blue-50 disabled:opacity-50 whitespace-nowrap"
    >
      {label}
    </button>
  );

  switch (est.estado) {
    case 'sem_comissao':
      return <span className="text-gray-400 text-xs">—</span>;
    case 'sem_fornecedor':
      return (
        <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded" style={{ backgroundColor: '#fde68a', color: '#000' }} title="Vincule a vendedora a um Fornecedor no cadastro de Usuario">
          <AlertTriangle size={12} /> Sem fornecedor
        </span>
      );
    case 'pendente':
      return btn('Gerar');
    case 'divergente':
      return (
        <span className="inline-flex flex-col items-center gap-1">
          <span className="inline-flex items-center gap-1 text-xs font-semibold" style={{ color: '#b45309' }} title={est.titulo ? `Titulo: R$ ${fmt(est.titulo.valor)} · Mensal (faixa+bonus): R$ ${fmt(est.valor_mensal)}` : ''}>
            <AlertTriangle size={12} /> Divergente
          </span>
          {btn('Regerar')}
        </span>
      );
    case 'gerado':
      return (
        <span className="inline-flex items-center gap-1 text-xs text-green-700 font-medium" title={est.titulo ? `${est.titulo.numero} · vence ${fmtDataPlain(est.titulo.data_vencimento)}` : ''}>
          <Check size={13} /> Gerado
        </span>
      );
    case 'pago':
      return (
        <span className="inline-flex items-center gap-1 text-xs text-gray-600 font-medium" title={est.titulo ? est.titulo.numero : ''}>
          <Check size={13} /> Pago
        </span>
      );
    default:
      return <span className="text-gray-300 text-xs">—</span>;
  }
}

// Sequencia de ponto de UMA semana — dentro do "+" daquela semana (so admin). Mostra so os
// dias da semana (dia_inicio..dia_fim) e repete a contagem de avisos que decide o bonus, pra
// a tela EXPLICAR por que o premio saiu ou caiu (nao "cair do ceu").
function PontoDaSemana({ dias, loading, semana }: { dias: DiaPonto[] | undefined; loading: boolean; semana: PremioSemana }) {
  if (loading || dias === undefined) return <span className="text-gray-400 text-xs">Carregando ponto...</span>;
  const diasSem = dias.filter((d) => d.dia >= semana.dia_inicio && d.dia <= semana.dia_fim);
  const avisos = (Number(semana.atrasos) || 0) + (Number(semana.anomalias) || 0);
  const min = Number(semana.atraso_minutos) || 0;
  const teto = Number(semana.teto_minutos) || 0;
  return (
    <div>
      {(avisos > 0 || min > 0) && (
        <div className="flex items-center flex-wrap gap-2 text-[11px] text-gray-500 mb-1">
          <span>Atrasos {semana.atrasos || 0} · Anomalias {semana.anomalias || 0}{min > 0 ? ` · ${min} min/sem${teto > 0 ? `/${teto}` : ''}` : ''}</span>
          {semana.bonus_perdido_avisos && (
            <span className="px-1.5 rounded text-[10px] font-semibold" style={{ backgroundColor: '#fdba74', color: '#000' }}>
              bonus perdido (2+ avisos)
            </span>
          )}
          {semana.bonus_perdido_minutos && (
            <span className="px-1.5 rounded text-[10px] font-semibold" style={{ backgroundColor: '#fdba74', color: '#000' }}>
              bonus perdido ({min} min ≥ teto {teto})
            </span>
          )}
        </div>
      )}
      {diasSem.length === 0
        ? <span className="text-gray-400 text-xs">Sem batidas nesta semana.</span>
        : diasSem.map((d) => <LinhaDiaPonto key={d.data} d={d} />)}
    </div>
  );
}

function PctBadge({ pct }: { pct: number }) {
  const v = Number(pct || 0);
  const color = v >= 100 ? 'bg-green-100 text-black' : v >= 70 ? 'bg-yellow-100 text-black' : 'bg-red-100 text-black';
  return (
    <span className={`cel-num inline-block whitespace-nowrap px-2 py-0.5 rounded text-[12.25px] font-semibold ${color}`}>
      {v.toFixed(1)}%
    </span>
  );
}
